import React from 'react';
import { Spin, Alert, Tabs, Card, Typography, Button, Collapse, Space, Empty } from 'antd';
import { LineChartOutlined, BarChartOutlined, UserOutlined, FieldTimeOutlined, ThunderboltOutlined, AppstoreOutlined, WarningOutlined, BugOutlined, CarOutlined, ScheduleOutlined } from '@ant-design/icons';
import OTRSummary from './OTRSummary';
import RoutePerformance from './RoutePerformance';
import TimeOfDayAnalysis from './TimeOfDayAnalysis';
import DriverPerformance from './DriverPerformance';
import TripStopPerformance from './TripStopPerformance';
import AdvancedInsights from './AdvancedInsights';
import ProposedTimetables from './ProposedTimetables';
import ProposedOTRWindowTimetables from './ProposedOTRWindowTimetables';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

/**
 * OTRReport Component
 * Displays a comprehensive OTR report with multiple tabs for different analytics views
 */
const OTRReport = ({ reportData, loading, error, onRetry, dateRange }) => {
  // Handle loading state
  if (loading) {
    return (
      <Card className="report-card">
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 20 }}>
            <Title level={4}>Generating OTR Report</Title>
            <p>This may take a moment for large files...</p>
          </div>
        </div>
      </Card>
    );
  }

  // Handle error state
  if (error) {
    // Split error message and stack trace if present
    const errorParts = error.split('\n\nStack:');
    const errorMessage = errorParts[0];
    const stackTrace = errorParts.length > 1 ? errorParts[1] : null;

    return (
      <Card className="report-card">
        <Alert
          message={<Space><BugOutlined /> Report Generation Error</Space>}
          description={
            <div>
              <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{errorMessage}</Paragraph>

              <Collapse ghost style={{ marginTop: 16 }}>
                {stackTrace && (
                  <Panel header="Technical Details" key="1">
                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', backgroundColor: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                      {stackTrace}
                    </pre>
                  </Panel>
                )}
                <Panel header="Troubleshooting Tips" key="2">
                  <ul>
                    <li>Check that your CSV files have the expected columns and format</li>
                    <li>Ensure all CSV files contain valid data with consistent formatting</li>
                    <li>Verify date formats in your CSV match ISO format (YYYY-MM-DD)</li>
                    <li>Check that numeric fields like varianceInSeconds contain valid numbers</li>
                    <li>Try with a smaller subset of data to identify problematic records</li>
                  </ul>
                </Panel>
              </Collapse>

              <div style={{ marginTop: 16 }}>
                <Button type="primary" onClick={() => window.location.reload()}>
                  Reload Page
                </Button>
              </div>
            </div>
          }
          type="error"
          showIcon
        />
      </Card>
    );
  }

  // Handle no data available for the selected date range
  if (!reportData?.summary) {
    return (
      <Card className="report-card">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size="middle" align="center">
                <Text strong>No On-Time Running report generated</Text>
                <Text>Generate a report to view on-time running performance for the selected date range</Text>
                <div style={{ marginTop: 16 }}>
                  <Button 
                    type="primary" 
                    icon={<BarChartOutlined />}
                    size="large"
                    onClick={onRetry || (() => window.location.reload())}
                  >
                    Generate Report
                  </Button>
                </div>
              </Space>
            }
          />
        </div>
      </Card>
    );
  }

  // Handle empty data
  if (reportData.summary.noData) {
    return (
      <Card className="report-card">
        <Alert
          message="No Report Data"
          description="Please select a date range and generate a report to view OTR analytics."
          type="info"
          showIcon
        />
      </Card>
    );
  }
  
  // Display warning if present
  const warning = reportData.warning;

  return (
    <Card className="report-card">
      {warning && (
        <Alert
          message={<Space><WarningOutlined /> Analysis Warnings</Space>}
          description={warning}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          closable
        />
      )}
      <Tabs defaultActiveKey="summary" type="card">
        <TabPane 
          tab={<span><AppstoreOutlined /> Summary</span>} 
          key="summary"
        >
          <OTRSummary reportData={reportData} />
        </TabPane>
        
        <TabPane 
          tab={<span><CarOutlined /> Trip & Stop</span>} 
          key="tripstop"
        >
          <TripStopPerformance reportData={reportData} dateRange={dateRange} />
        </TabPane>
        
        <TabPane 
          tab={<span><BarChartOutlined /> Route Performance</span>} 
          key="routes"
        >
          <RoutePerformance reportData={reportData} />
        </TabPane>
        
        <TabPane 
          tab={<span><FieldTimeOutlined /> Time of Day</span>} 
          key="timeOfDay"
        >
          <TimeOfDayAnalysis reportData={reportData} />
        </TabPane>
        
        <TabPane 
          tab={<span><UserOutlined /> Driver Performance</span>} 
          key="drivers"
        >
          <DriverPerformance reportData={reportData} />
        </TabPane>
        
        {/* <TabPane 
          tab={<span><ScheduleOutlined /> Proposed Timetables</span>} 
          key="proposedTimetables"
        >
          <ProposedTimetables reportData={reportData} />
        </TabPane> */}
        
        <TabPane
          tab={<span><ScheduleOutlined /> OTR Window Timetables</span>}
          key="proposedOTRWindowTimetables"
        >
          <ProposedOTRWindowTimetables reportData={reportData} dateRange={dateRange} />
        </TabPane>
        
        {/* <TabPane 
          tab={<span><ThunderboltOutlined /> Advanced Insights</span>} 
          key="advanced"
        >
          <AdvancedInsights reportData={reportData} />
        </TabPane> */}
      </Tabs>
    </Card>
  );
};

export default OTRReport; 